import React from 'react';

import { listingSourcesApi } from '../../requests/listingSources';
import { CountChips, Chevron, ModePill, OutcomePill, StatusPill } from './Pills';
import { formatClock, formatNumber, formatRange, formatRunTime, zoneAbbreviation } from './format';

const LISTINGS_PER_PAGE = 50;

// The design only searches once the query could be an id fragment worth
// asking about; one digit matches half the database.
const SEARCH_MIN_LENGTH = 3;

/**
 * The morning check, and everything reached from it.
 *
 * Nine visits in ten are one question — did last night's run go, and did
 * anything odd happen — so that answer is the first thing on the page and
 * needs no clicks. The history below it is for the tenth.
 */
export default class RunOverview extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            runs: [],
            runsMeta: null,
            page: 1,
            loadingRuns: true,
            runsError: null,

            query: '',
            results: null,
            searching: false,

            unmappedOpen: false,
            unmapped: null,
        };

        this.onQuery = this.onQuery.bind(this);
        this.clearQuery = this.clearQuery.bind(this);
        this.toggleUnmapped = this.toggleUnmapped.bind(this);
    }

    componentDidMount() {
        this.loadRuns(1);
        this.syncTicker();
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.searchTimer);
        clearInterval(this.ticker);
    }

    componentDidUpdate(previous) {
        // A run finishing changes what the history says, so the list is
        // reloaded when the parent tells us the source moved on.
        if (previous.refreshToken !== this.props.refreshToken) {
            this.loadRuns(this.state.page);
        }

        this.syncTicker();
    }

    /** A one-second tick, held only while a run is going, so "refreshed Ns
        ago" under the progress bar counts without anything else re-rendering
        a static page all day. */
    syncTicker() {
        const running = this.props.source.last_run && this.props.source.last_run.status === 'running';

        if (running && !this.ticker) {
            this.ticker = setInterval(() => this.forceUpdate(), 1000);
        } else if (!running && this.ticker) {
            clearInterval(this.ticker);
            this.ticker = null;
        }
    }

    loadRuns(page) {
        this.setState({ loadingRuns: true, runsError: null });

        listingSourcesApi.runs(this.props.source.key, page).then(
            (envelope) => {
                if (this.unmounted) return;
                this.setState({
                    runs: envelope.data,
                    runsMeta: envelope.meta && envelope.meta.pagination,
                    page,
                    loadingRuns: false,
                });
            },
            (error) => {
                if (this.unmounted) return;
                this.setState({ loadingRuns: false, runsError: error.message });
            }
        );
    }

    onQuery(event) {
        const query = event.target.value;
        this.setState({ query });
        clearTimeout(this.searchTimer);

        if (query.trim().length < SEARCH_MIN_LENGTH) {
            this.setState({ results: null, searching: false });
            return;
        }

        this.setState({ searching: true });

        // Typed a digit at a time, so the request waits for a pause rather
        // than firing seven times for a seven-digit listing id.
        this.searchTimer = setTimeout(() => {
            listingSourcesApi.search(this.props.source.key, query.trim()).then(
                (data) => {
                    if (this.unmounted) return;
                    this.setState({ results: data.results, searching: false });
                },
                () => {
                    if (this.unmounted) return;
                    this.setState({ results: [], searching: false });
                }
            );
        }, 350);
    }

    clearQuery() {
        clearTimeout(this.searchTimer);
        this.setState({ query: '', results: null, searching: false });
    }

    toggleUnmapped() {
        const open = !this.state.unmappedOpen;
        this.setState({ unmappedOpen: open });

        if (open && this.state.unmapped === null) {
            listingSourcesApi.unmapped(this.props.source.key).then(
                (data) => !this.unmounted && this.setState({ unmapped: data.unmapped }),
                () => !this.unmounted && this.setState({ unmapped: [] })
            );
        }
    }

    /**
     * The ceiling a run works to: pages of fifty, as the operator set it —
     * times every stream, because the page ceiling applies to each saved
     * search in each state rather than to the run as a whole. Counted
     * run-wide, a four-search five-state scope would otherwise show a full bar
     * and "~0s left" from the first stream onwards.
     */
    ceiling() {
        const settings = this.props.source.settings;
        const streams = Math.max(1, (settings.searches || []).length * (settings.states || []).length);

        return (settings.max_pages || 1) * LISTINGS_PER_PAGE * streams;
    }

    renderProgress(run) {
        const ceiling = this.ceiling();
        const settings = this.props.source.settings;
        const searches = (settings.searches || []).length;
        const states = (settings.states || []).length;

        // The strip is a bar drawn in segments, not a page counter — one
        // segment per page stopped meaning anything once the ceiling became
        // per search and per state.
        const segments = Math.max(1, Math.min(30, settings.max_pages || 1));
        const read = Math.min(run.processed || 0, ceiling);
        const percent = ceiling ? Math.round((read / ceiling) * 100) : 0;

        // The estimate comes from the run's own pace so far; before anything
        // has been read there is no pace to project from.
        const elapsed = run.started_at ? Math.max(1, (Date.now() - new Date(run.started_at)) / 1000) : 0;
        const rate = elapsed ? read / elapsed : 0;
        const eta = rate > 0 ? Math.ceil((ceiling - read) / rate) : null;

        const refreshedAt = this.props.refreshedAt;
        const age = refreshedAt ? Math.max(0, Math.round((Date.now() - refreshedAt) / 1000)) : 0;

        return (
            <div className="ls-progress">
                <div className="ls-progress__head">
                    <span className="ls-progress__read">{formatNumber(read)}</span>
                    <span className="ls-progress__scale">of ≤ {formatNumber(ceiling)} listings read</span>
                    <span className="ls-progress__percent">{percent}%</span>
                    {eta !== null && (
                        <span className="ls-progress__eta">
                            {eta > 60 ? '~' + Math.ceil(eta / 60) + ' min left' : '~' + eta + 's left'}
                        </span>
                    )}
                </div>
                <div className="ls-progress__pages">
                    {Array.apply(null, { length: segments }).map((ignored, index) => {
                        const per = ceiling / segments;
                        const fill = per > 0 ? Math.max(0, Math.min(1, (read - index * per) / per)) : 0;
                        return (
                            <div key={index} className="ls-progress__page">
                                <div
                                    className={'ls-progress__fill' + (fill === 1 ? ' is-full' : '')}
                                    style={{ width: Math.round(fill * 100) + '%' }}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="ls-progress__foot">
                    <span className="ls-progress__pagelabel">
                        {searches} search{searches === 1 ? '' : 'es'} × {states} state{states === 1 ? '' : 's'}
                    </span>
                    <span className="ls-progress__live" />
                    <span className="ls-progress__fresh">refreshed {age}s ago</span>
                </div>
            </div>
        );
    }

    renderHero() {
        const { source, onOpenRun, onRunNow } = this.props;
        const run = source.last_run;

        if (!run) {
            return (
                <div className="ls-card ls-hero ls-hero--empty">
                    <div className="ls-hero__kicker">No runs yet</div>
                    <p className="ls-hero__empty">
                        Nothing has been pulled from {source.label} yet. Start one by hand, or turn the nightly
                        run on and it will go tonight.
                    </p>
                </div>
            );
        }

        const running = run.status === 'running';
        const zone = zoneAbbreviation(source.settings.timezone);

        return (
            <div className="ls-card ls-hero">
                {run.mode === 'dry_run' && (
                    <div className="ls-hero__dryband">
                        <span className="ls-hero__dryband-tag">Dry run</span>
                        <span>Nothing was written</span>
                    </div>
                )}

                <div className="ls-hero__head">
                    <div className="ls-hero__headings">
                        <div className="ls-hero__kicker">{running ? 'Running now' : 'Last run'}</div>
                        <div className="ls-hero__meta">
                            <StatusPill status={run.status} />
                            <ModePill mode={run.mode} />
                            <span className="ls-hero__time">
                                {running
                                    ? 'started ' + formatClock(run.started_at) + ' ' + zone + ' · ' + run.started_by
                                    : formatRunTime(run.started_at) + ' ' + zone +
                                      (run.finished_at ? ' · finished ' + formatClock(run.finished_at) : '') +
                                      ' · ' + run.started_by}
                            </span>
                        </div>
                    </div>
                    <button type="button" className="ls-btn ls-btn--quiet" onClick={() => onOpenRun(run)}>
                        {running ? 'Watch run' : 'Open run'}
                        <Chevron />
                    </button>
                </div>

                {running && this.renderProgress(run)}

                <CountChips run={run} large />

                {run.error && (
                    <div className="ls-alert ls-alert--error ls-hero__error">
                        <span className="ls-alert__mark">!</span>
                        <span className="ls-alert__text">{run.error}</span>
                        <button type="button" className="ls-alert__retry" onClick={() => onRunNow('write')}>
                            Run again
                        </button>
                    </div>
                )}

                {!source.settings.enabled && (
                    <div className="ls-hero__offband">
                        <span>No run tonight — the schedule is off.</span>
                        <button type="button" className="ls-btn ls-btn--small" onClick={this.props.onOpenSettings}>
                            Turn on
                        </button>
                    </div>
                )}
            </div>
        );
    }

    renderSearch() {
        const { query, results, searching } = this.state;

        if (query.trim().length < SEARCH_MIN_LENGTH) return null;

        if (searching) {
            return <div className="ls-card ls-search__panel ls-search__panel--quiet">Searching…</div>;
        }

        if (!results) return null;

        if (results.length === 0) {
            return (
                <div className="ls-card ls-search__panel">
                    <div className="ls-search__headline">No match for {query.trim()}</div>
                </div>
            );
        }

        return (
            <div className="ls-card ls-search__panel">
                <div className="ls-search__headline">
                    {results.length === 1 ? '1 listing' : results.length + ' listings'} matching “{query.trim()}”
                </div>
                {results.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className="ls-search__hit"
                        onClick={() => this.props.onOpenRun(item.run)}
                    >
                        <span className="ls-mono ls-search__id">{item.external_id}</span>
                        <OutcomePill outcome={item.outcome} />
                        <span className="ls-search__reason">{item.reason}</span>
                        <span className="ls-search__where">
                            run #{item.run.id} · {formatRunTime(item.run.started_at)}
                        </span>
                    </button>
                ))}
            </div>
        );
    }

    renderHistory() {
        const { runs, runsMeta, loadingRuns, runsError, page } = this.state;

        if (runsError) {
            return <div className="ls-card ls-empty">Could not load the run history. {runsError}</div>;
        }

        if (loadingRuns && runs.length === 0) {
            return <div className="ls-card ls-empty">Loading runs…</div>;
        }

        if (runs.length === 0) {
            return <div className="ls-card ls-empty">No runs recorded yet.</div>;
        }

        const lastPage = runsMeta ? runsMeta.last_page : 1;

        return (
            <div className="ls-card">
                <div className="ls-table__scroll">
                    <div className="ls-table ls-table--runs">
                        <div className="ls-table__head">
                            <div>Run</div>
                            <div>Status</div>
                            <div>Mode</div>
                            <div className="is-numeric">Created</div>
                            <div className="is-numeric">Updated</div>
                            <div className="is-numeric">Unchanged</div>
                            <div className="is-numeric">Left alone</div>
                            <div className="is-numeric">Failed</div>
                            <div>Started by</div>
                            <div />
                        </div>

                        {runs.map((run) => (
                            <div
                                key={run.id}
                                className={'ls-table__row' + (run.status === 'running' ? ' is-running' : '')}
                                onClick={() => this.props.onOpenRun(run)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(event) => event.key === 'Enter' && this.props.onOpenRun(run)}
                            >
                                <div>
                                    <div className="ls-table__primary">{formatRunTime(run.started_at)}</div>
                                    <div className="ls-mono ls-table__sub">run #{run.id}</div>
                                </div>
                                <div><StatusPill status={run.status} cell /></div>
                                <div><ModePill mode={run.mode} cell /></div>
                                {['created', 'updated', 'unchanged', 'skipped', 'failed'].map((key) => (
                                    <div
                                        key={key}
                                        className={[
                                            'ls-mono is-numeric',
                                            run[key] ? '' : 'is-zero',
                                            key === 'failed' && run[key] ? 'is-bad' : '',
                                            run.mode === 'dry_run' && run[key] ? 'is-dry' : '',
                                        ].filter(Boolean).join(' ')}
                                    >
                                        {formatNumber(run[key])}
                                    </div>
                                ))}
                                <div className="ls-table__by">{run.started_by}</div>
                                <div className="ls-table__chevron"><Chevron /></div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ls-pager">
                    <span className="ls-pager__label">{formatRange(runsMeta, runs.length)}</span>
                    <button
                        type="button"
                        className="ls-btn ls-btn--small"
                        disabled={page <= 1}
                        onClick={() => this.loadRuns(page - 1)}
                    >
                        Newer
                    </button>
                    <button
                        type="button"
                        className="ls-btn ls-btn--small"
                        disabled={page >= lastPage}
                        onClick={() => this.loadRuns(page + 1)}
                    >
                        Older
                    </button>
                </div>
            </div>
        );
    }

    renderUnmapped() {
        const { unmappedOpen, unmapped } = this.state;
        const source = this.props.source;

        const summary = unmapped === null
            ? 'Unmapped vocabulary'
            : unmapped.length === 0
                ? 'Unmapped vocabulary · none in the last 30 runs'
                : 'Unmapped vocabulary · ' + unmapped.length + ' words, ' +
                  formatNumber(unmapped.reduce((total, row) => total + row.total, 0)) + ' listings';

        return (
            <div className="ls-unmapped">
                <button
                    type="button"
                    className="ls-unmapped__toggle"
                    aria-expanded={unmappedOpen}
                    onClick={this.toggleUnmapped}
                >
                    <svg
                        className={'ls-unmapped__caret' + (unmappedOpen ? ' is-open' : '')}
                        width="11" height="11" viewBox="0 0 11 11" fill="none"
                        stroke="currentColor" strokeWidth="1.6" aria-hidden="true"
                    >
                        <path d="M3 4.3 L5.5 6.8 L8 4.3" />
                    </svg>
                    {summary}
                </button>

                {unmappedOpen && (
                    <div className="ls-card ls-unmapped__panel">
                        <div className="ls-unmapped__head">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
                                strokeWidth="1.5" aria-hidden="true">
                                <rect x="2.4" y="5.4" width="7.2" height="5" rx="1" />
                                <path d="M4.2 5.4 V3.9 A1.8 1.8 0 0 1 7.8 3.9 V5.4" />
                            </svg>
                            <span className="ls-mono">config/{source.key}.php</span>
                        </div>

                        {unmapped === null && <div className="ls-unmapped__empty">Loading…</div>}
                        {unmapped && unmapped.length === 0 && (
                            <div className="ls-unmapped__empty">Nothing unmapped in the last 30 runs.</div>
                        )}
                        {(unmapped || []).map((row) => (
                            <div key={row.reason} className="ls-unmapped__row">
                                <span className="ls-unmapped__reason">{row.reason}</span>
                                <span className="ls-mono ls-unmapped__column">{row.column}</span>
                                <span className="ls-mono ls-unmapped__total">{formatNumber(row.total)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { query } = this.state;

        return (
            <div className="ls-overview">
                {this.renderHero()}

                <div className="ls-sectionhead">
                    <h2 className="ls-sectionhead__title">Run history</h2>
                    <div className="ls-search">
                        <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="#7A8698"
                            strokeWidth="1.6" aria-hidden="true">
                            <circle cx="6" cy="6" r="4.4" />
                            <path d="M9.4 9.4 L12.6 12.6" />
                        </svg>
                        <input
                            type="text"
                            value={query}
                            onChange={this.onQuery}
                            placeholder="Property or listing id"
                            aria-label="Find a listing by property id or listing id"
                        />
                        {query && (
                            <button type="button" className="ls-search__clear" onClick={this.clearQuery} aria-label="Clear">
                                ×
                            </button>
                        )}
                    </div>
                </div>

                {this.renderSearch()}
                {this.renderHistory()}
                {this.renderUnmapped()}
            </div>
        );
    }
}
